// The observer's wall display for Snooker: a "now throwing" panel for the
// player at the oche (name, score, current break or dart count), what's on,
// the other players as a compact list, and a dartboard with the balls still
// on the table painted on their wedges. Styled by the .obs-sn rules in
// styles.css; controller.js works out the game state and passes it in.
import { RED_NUMBERS, COLORS, RED_COLOR } from './definition.js';

const escapeHTML = text => String(text).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

// Same clockwise-from-the-top wedge order controller.js and dartboard.svg use.
const WEDGE_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
const COLOR_BY_NUMBER = new Map(COLORS.map(c => [c.number, c]));

const point = (radius, angleDeg) => {
    const angle = angleDeg * Math.PI / 180;
    return [250 + radius * Math.sin(angle), 250 - radius * Math.cos(angle)];
};

function sector(innerR, outerR, startDeg, endDeg) {
    const [x1, y1] = point(outerR, startDeg);
    const [x2, y2] = point(outerR, endDeg);
    const [x3, y3] = point(innerR, endDeg);
    const [x4, y4] = point(innerR, startDeg);
    return `M${x1} ${y1}A${outerR} ${outerR} 0 0 1 ${x2} ${y2}L${x3} ${y3}A${innerR} ${innerR} 0 0 0 ${x4} ${y4}Z`;
}

// A snooker ball on the board: a white ring when it's on, dimmed when not,
// with a soft highlight for the shine.
function boardBall(x, y, radius, color, isOn) {
    const ring = isOn ? `<circle cx="${x}" cy="${y}" r="${radius + 5}" fill="none" stroke="#fff" stroke-width="3.5"/>` : '';
    return `<g opacity="${isOn ? 1 : 0.34}">${ring}` +
        `<circle cx="${x}" cy="${y}" r="${radius}" fill="${color}" stroke="#ffffff" stroke-opacity="0.25" stroke-width="1.5"/>` +
        `<ellipse cx="${x - radius * 0.3}" cy="${y - radius * 0.35}" rx="${radius * 0.32}" ry="${radius * 0.2}" fill="#fff" fill-opacity="0.38"/></g>`;
}

const emptySlot = (x, y, radius) =>
    `<circle cx="${x}" cy="${y}" r="${radius}" fill="none" stroke="#fff" stroke-opacity="0.14" stroke-width="2" stroke-dasharray="4 4"/>`;

function boardSVG({ targets, removedReds, colorsGone, ballRadius }) {
    const targetSet = new Set(targets);
    let svg = '<svg class="obs-sn-svg" viewBox="0 0 500 500" role="img" aria-label="Dartboard showing the balls still on the table">' +
        '<circle cx="250" cy="250" r="248" fill="#0d1113"/>';

    WEDGE_ORDER.forEach((number, i) => {
        const start = i * 18 - 9;
        const end = i * 18 + 9;
        const odd = i % 2;
        const isLit = targetSet.has(number);
        const single = odd ? '#2d353b' : '#252c31';
        const ring = odd ? '#24493f' : '#1c3b33';
        svg += `<path d="${sector(30, 105, start, end)}" fill="${single}"/><path d="${sector(105, 135, start, end)}" fill="${ring}"/>` +
            `<path d="${sector(135, 185, start, end)}" fill="${single}"/><path d="${sector(185, 215, start, end)}" fill="${ring}"/>`;
        if (isLit) svg += `<path d="${sector(30, 215, start, end)}" fill="#fff" fill-opacity="0.13"/>`;
        const [tx, ty] = point(232, i * 18);
        svg += `<text x="${tx}" y="${ty}" fill="${isLit ? '#fff' : '#8A96A3'}" font-size="21" font-weight="700" text-anchor="middle" dominant-baseline="central" font-family="Exo,Sora,sans-serif">${number}</text>`;
    });
    svg += '<circle cx="250" cy="250" r="30" fill="#24493f"/>';

    WEDGE_ORDER.forEach((number, i) => {
        const [x, y] = point(162, i * 18);
        const isRed = RED_NUMBERS.includes(number);
        const color = COLOR_BY_NUMBER.get(number);
        if (isRed ? removedReds.includes(number) : colorsGone.has(number)) {
            svg += emptySlot(x, y, ballRadius);
            return;
        }
        svg += boardBall(x, y, ballRadius, isRed ? RED_COLOR : color.color, targetSet.has(number));
    });

    const black = COLOR_BY_NUMBER.get(25);
    svg += colorsGone.has(25) ? emptySlot(250, 250, 22) : boardBall(250, 250, 24, black.color, targetSet.has(25));
    return svg + '</svg>';
}

const ballHTML = (color, size) => `<span class="obs-sn-ball" style="--c:${color};--d:${size}"></span>`;

// Returns { html, classes } - the caller sets the board's innerHTML and
// toggles the classes on the .obs-sn element itself.
//   players, scores        scores[player] = points
//   activeIndex            whose turn it is
//   matchComplete          the match is over (no active player any more)
//   turnMode               'break' | 'alternate'
//   currentBreak           points gained so far this turn (Break mode)
//   dartNumber             1-based dart of this turn (Alternate turns)
//   onKind                 'red' | 'color' | 'next' - what the active player may pot
//   onLabel                that, as text ("Red", "Any color", "Blue")
//   targets                wedge numbers currently on
//   removedReds            reds already potted
//   colorsGone             Set of colour numbers already potted for good
//   nextColor              the colour that's next, in the colors-only phase
//   subtitle               the header's second half, as HTML
export function snookerObserverBoard({
    players, scores, activeIndex, matchComplete, turnMode, currentBreak, dartNumber,
    onKind, onLabel, targets, removedReds, colorsGone, nextColor, subtitle
}) {
    const accentOf = i => `--a:var(--player-${(i % 5) + 1})`;
    const n = players.length;
    const otherIndexes = players.map((_, i) => i).filter(i => i !== activeIndex);

    let banner;
    if (matchComplete) {
        const best = Math.max(...players.map(p => scores[p]));
        const winners = players.map((p, i) => i).filter(i => scores[players[i]] === best);
        banner = `
            <div class="obs-sn-banner" style="${accentOf(winners[0])}">
                <div class="obs-sn-label">Game over</div>
                <div class="obs-sn-name">${winners.map(i => escapeHTML(players[i])).join(' &amp; ')} ${winners.length > 1 ? 'tie' : 'wins'}</div>
                <div class="obs-sn-figures"><div><div class="obs-sn-label muted">Score</div><div class="obs-sn-score">${best}</div></div></div>
            </div>`;
    } else {
        const active = players[activeIndex];
        const third = turnMode === 'break'
            ? `<div class="obs-sn-label muted">Current break</div><div class="obs-sn-break">${currentBreak}</div>`
            : `<div class="obs-sn-label muted">Dart</div><div class="obs-sn-break">${Math.min(dartNumber, 3)}<small> of 3</small></div>`;
        const chipBalls = onKind === 'red'
            ? ballHTML(RED_COLOR, 40)
            : onKind === 'color'
                ? COLORS.map(c => ballHTML(c.color, 32)).join('')
                : ballHTML(nextColor.color, 40);
        banner = `
            <div class="obs-sn-banner" style="${accentOf(activeIndex)}">
                <div class="obs-sn-nowrow">
                    <div>
                        <div class="obs-sn-label">Now throwing</div>
                        <div class="obs-sn-name">${escapeHTML(active)}</div>
                    </div>
                    <div class="obs-sn-chip obs-sn-chip-in"><span class="obs-sn-label muted">On</span>${chipBalls}<span>${escapeHTML(onLabel)}</span></div>
                </div>
                <div class="obs-sn-figures">
                    <div><div class="obs-sn-label muted">Score</div><div class="obs-sn-score">${scores[active]}</div></div>
                    <div>${third}</div>
                </div>
            </div>
            <div class="obs-sn-chip obs-sn-chip-out"><span class="obs-sn-label muted">On</span>${chipBalls}<span>${escapeHTML(onLabel)}</span></div>`;
    }

    const others = otherIndexes.map(i => `
        <div class="obs-sn-other${matchComplete && scores[players[i]] === Math.max(...players.map(p => scores[p])) ? ' winner' : ''}" style="${accentOf(i)}">
            <span class="obs-sn-who"><span class="obs-sn-dot"></span><span class="obs-sn-otname">${escapeHTML(players[i])}</span></span>
            <span class="obs-sn-otscore">${scores[players[i]]}</span>
        </div>`).join('');

    return {
        classes: { 'obs-sn-dense': n > 3 },
        html: `
            <div class="obs-head"><span>Snooker <span class="obs-sub">&middot; ${subtitle}</span></span></div>
            <div class="obs-sn-body" style="--sn-lcols:${otherIndexes.length >= 4 ? 2 : 1};--sn-pcols:${Math.max(1, Math.min(otherIndexes.length, 3))}">
                <div class="obs-sn-side">${banner}<div class="obs-sn-others">${others}</div></div>
                <div class="obs-sn-boardbox">${boardSVG({ targets: matchComplete ? [] : targets, removedReds, colorsGone, ballRadius: 18 })}</div>
            </div>`
    };
}
